import React from 'react';
import { Container, Row, Col, Card, Stack, Image, Button } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const OverviewPage = () => {
	return (
		<Container className="py-0">
			<Helmet>
				<title>Quick Start - QuickyChat</title>
				<meta name="description" content="Get started with QuickyChat by following our comprehensive guide to create and deploy AI chatbots." />
				<meta name="keywords" content="QuickyChat, AI chatbot, create chatbot, deploy chatbot, chatbot guide" />
			</Helmet>
			<Row>
				{/* Sidebar */}
				<Col md={3} className="rounded p-3" style={{ backgroundColor: '#2d2d2d' }}>
					<h5 className="text-light">Get Started</h5>
					<ul className="list-unstyled">
						<li><a href="#Introduction" className="text-light">Introduction</a></li>
						<li><a href="#Account" className="text-light">Create Account</a></li>
						<li><a href="#Agents" className="text-light">Create AI Agent</a></li>
						<li><a href="#KnowledgeBases" className="text-light">Add Knowledge Bases</a></li>
						<li><a href="#Configure" className="text-light">Configure Chatbot</a></li>
						<li><a href="#Deploy" className="text-light">Deploy Chatbot</a></li>
						{/* <li><a href="#Subscription" className="text-light">Subscription</a></li> */}
					</ul>
					<h5 className="text-light mt-4">Learn More</h5>
					<ul className="list-unstyled">
						{/* <li><Link to="/overview" className="text-light">Overview</Link></li> */}
						<li><Link to="/solutions" className="text-light">Solutions</Link></li>
						<li><Link to="/whatsapp" className="text-light">WhatsApp</Link></li>
						<li><Link to="/pricing" className="text-light">Pricing</Link></li>
						<li><Link to="/demo" className="text-light">Demo</Link></li>
					</ul>
				</Col>

				{/* Main Content */}
				<Col md={9} className="p-4">

					{/* Intro */}
					<Stack id="Introduction">
						<h1 className="mb-4">Quicky Start</h1>
						<p>
							Welcome to the documentation for our chatbot customization platform! This guide will walk you through the 
							essential steps to create and deploy AI chatbots tailored to your needs.
						</p>

						{/* Highlight Card */}
						<Card className="text-light mb-4" style={{ backgroundColor: '#2d2d2d' }}>
							<Card.Body>
							<Card.Title>You will learn:</Card.Title>
							<ul>
								<li>How to create and configure AI agents</li>
								<li>How to add and manage custom knowledge bases</li>
								<li>How to customize agent behavior and responses</li>
								<li>How to deploy your chatbot to various platforms</li>
							</ul>
							</Card.Body>
						</Card>
					</Stack>

					{/* Create account */}
					<Stack id='Account'>
						<h3>Creating Your Account</h3>
						<p>
							To get started, create an account on our platform. You'll be guided through the setup process to define 
							your organization and initialize your chatbot projects.
						</p>
						<Row className='align-items-center'>
							<Col md={6}>
							<Card className="text-light mb-4" style={{ backgroundColor: '#2d2d2d' }}>
							<Card.Body>
								<Image src='/images/quickychat-login.png' fluid/>
							</Card.Body>
							</Card>
								
							</Col>
							<Col  md={6}>
								<ul>
									<li>Register for an account using your email</li>
									<li>Login to your account and start using platform</li>
									<li>Create an organization, if you haven't during account registration.</li>
								</ul>
							</Col>
						</Row>
					</Stack>

					{/* Create agents */}
					<Stack id='Agents'>
						<h3>Creating AI Agents</h3>
						<p>
							Once your account is set up, create your first AI Agent. Choose a name and description. 
							You can add custom rules and configurations later.
						</p>
						<Row className='align-items-center'>
							<Col md={6}>
							<Card className="text-light mb-4" style={{ backgroundColor: '#2d2d2d' }}>
							<Card.Body>
								<Image src='/images/create-agent.png' fluid/>
							</Card.Body>
							</Card>
								
							</Col>
							<Col  md={6}>
								{/* <ul>
									<li>Register for an account using your email</li>
									<li>Login to your account and start using platform</li>
									<li>Create an organization, if you haven't during account registration.</li>
								</ul> */}
							</Col>
						</Row>
					</Stack>

					{/* Add knowledge base */}
					<Stack id='KnowledgeBases'>
						<h3>Adding Knowledge Bases</h3>
						<p>
							Enhance your chatbot's capabilities by uploading files, adding text content, inserting links, or creating 
							FAQs. Your chatbot will use this information to provide accurate responses.
						</p>
						<Row className='align-items-center'>
							<Col md={6}>
							<Card className="text-light mb-4" style={{ backgroundColor: '#2d2d2d' }}>
							<Card.Body>
								<Image src='/images/add-data.png' fluid/>
							</Card.Body>
							</Card>
								
							</Col>
							<Col  md={6}>
								{/* <ul>
									<li>Register for an account using your email</li>
									<li>Login to your account and start using platform</li>
									<li>Create an organization, if you haven't during account registration.</li>
								</ul> */}
							</Col>
						</Row>
					</Stack>

					{/* configure chatbot */}
					<Stack id='Configure'>
						<h3>Configuring Your Chatbot</h3>
						<p>
							Personalize your chatbot to fit your brand or project requirements. 
							During configuration, you can updating the following:
						</p>
						<ul>
							<li><strong>Display Name:</strong> Set the name your users will see when interacting with the chatbot.</li>
							<li><strong>Avatar:</strong> Upload a custom avatar to give your chatbot a unique identity.</li>
							<li><strong>Greeting Message:</strong> Define the initial message your chatbot sends to greet users.</li>
							<li><strong>Custom Colors:</strong> Adjust the chatbot's color scheme to match your brand or application design.</li>
							<li><strong>System Prompt:</strong> Set a background instruction that shapes how your chatbot responds to users.</li>
						</ul>
						<p>
							These options allow you to create a tailored user experience, making your chatbot feel more engaging and aligned with your needs.
						</p>
					</Stack>

					{/*Deploy chatbot */}
					<Stack id='Deploy'>
						<h3>Deploying Your Chatbot</h3>
						<p>
							After customization, deploy your chatbot to platforms like websites, apps, or communication tools. We 
							provide integration options for seamless deployment.
						</p>
						<Row className='align-items-center'>
							<Col md={6}>
							<Card className="text-light mb-4" style={{ backgroundColor: '#2d2d2d' }}>
							<Card.Body>
								<Image src='/images/deploy-script.png' fluid/>
							</Card.Body>
							</Card>
								
							</Col>
							<Col  md={6}>
								{/* <ul>
									<li>Register for an account using your email</li>
									<li>Login to your account and start using platform</li>
									<li>Create an organization, if you haven't during account registration.</li>
								</ul> */}
							</Col>
						</Row>
					</Stack>
				</Col>
			</Row>
			{/* Call to Action */}
			<Row className="text-center py-5">
				<Col>
				<h3 className="mb-3">Ready to Get Started?</h3>
				<p>
					Sign up today and begin creating intelligent, customized AI chatbots that work for you.
				</p>
				<Link to="/dashboard">
					<Button variant="primary" size="lg">
						Start Building for Free
					</Button>
				</Link>
				</Col>
			</Row>
		</Container>
	);
};

export default OverviewPage;
